import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import {
  calculateSelectedAreaFocusTransform,
  viewerQueryKeys,
  type PdfRasterFallback,
} from '@ironview/interactive-viewer';
import {
  Layout,
  PhotoMarker,
  Unit,
  Position,
  ImageDimensions,
  SaveMarkersResponse,
  SaveMarkersData,
  LayoutHotspotListItem,
} from '../types/types';
import TopControls from './superadmin/TopControls';
import LayoutCanvas from './LayoutCanvas';
import { HeatMapLegend } from './HeatMapLegend';
import StatusBar from './superadmin/StatusBar';
import { LogoLoading } from '@/shared/ui/custom/logo-loading';
import { Ironview360AreaTagControls } from './Ironview360AreaTagControls';
import { Button } from '@/shared/ui/button';
import { ImagePlus, Thermometer } from 'lucide-react';
import { useProperty } from '@/context/PropertyContext';
import { AddLayoutPhotoDialog } from './AddLayoutPhotoDialog';
import { useUser } from '@/shared/hooks/useUser';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { useDeleteViewerAreaTag, useUpdateViewerAreaTag } from '../hooks/useAreaTagCommands';
import {
  AreaTagSetLassoInput,
  selectAreaTagsMostlyInsideLasso,
  useAreaTagSets,
  useCreateAreaTagSet,
} from '@/features/area-tag-sets';
import { useToast } from '@/shared/hooks/use-toast';
import {
  HeatMapIssueRange,
  HeatMapSummary,
  buildPhotoHotspotCounts,
  buildPhotoIssueCounts,
  summarizePhotoIds,
} from '../utils/heatMap';
import { ironview360ViewerAdapter } from '../adapters/ironview360DataAdapter';
import { resolveAreaFocusViewport, resolvePdfOverviewFit } from './areaFocusViewport';
import { ErrorState } from '@/shared/errors/ErrorState';
import { getLayoutCollectionState } from './layoutCollectionState';

const normalizePolygon = (points: number[][]): number[][] => {
  if (!points || points.length === 0) return [];
  const normalized = points.map((p) => [Number(p[0]), Number(p[1])]);
  if (normalized.length > 1) {
    const [fx, fy] = normalized[0];
    const [lx, ly] = normalized[normalized.length - 1];
    if (fx === lx && fy === ly) return normalized.slice(0, -1);
  }
  return normalized;
};

const polygonsOverlap = (polyA: number[][], polyB: number[][]): boolean => {
  const a = normalizePolygon(polyA);
  const b = normalizePolygon(polyB);
  if (a.length < 3 || b.length < 3) return false;

  const orient = (p: number[], q: number[], r: number[]) => {
    const v = (q[1] - p[1]) * (r[0] - q[0]) - (q[0] - p[0]) * (r[1] - q[1]);
    if (Math.abs(v) < 1e-10) return 0;
    return v > 0 ? 1 : 2;
  };
  const onSeg = (p: number[], q: number[], r: number[]) =>
    q[0] <= Math.max(p[0], r[0]) + 1e-10 &&
    q[0] >= Math.min(p[0], r[0]) - 1e-10 &&
    q[1] <= Math.max(p[1], r[1]) + 1e-10 &&
    q[1] >= Math.min(p[1], r[1]) - 1e-10;
  const intersects = (p1: number[], q1: number[], p2: number[], q2: number[]) => {
    const o1 = orient(p1, q1, p2);
    const o2 = orient(p1, q1, q2);
    const o3 = orient(p2, q2, p1);
    const o4 = orient(p2, q2, q1);
    if (o1 !== o2 && o3 !== o4) return true;
    if (o1 === 0 && onSeg(p1, p2, q1)) return true;
    if (o2 === 0 && onSeg(p1, q2, q1)) return true;
    if (o3 === 0 && onSeg(p2, p1, q2)) return true;
    if (o4 === 0 && onSeg(p2, q1, q2)) return true;
    return false;
  };
  const pointOnSegment = (p: number[], a: number[], b: number[]) => {
    const cross = (p[1] - a[1]) * (b[0] - a[0]) - (p[0] - a[0]) * (b[1] - a[1]);
    if (Math.abs(cross) > 1e-10) return false;
    const dot = (p[0] - a[0]) * (b[0] - a[0]) + (p[1] - a[1]) * (b[1] - a[1]);
    if (dot < -1e-10) return false;
    const lenSq = (b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2;
    if (dot - lenSq > 1e-10) return false;
    return true;
  };
  const strictIntersects = (p1: number[], q1: number[], p2: number[], q2: number[]) => {
    // Shared boundaries/vertices are allowed; only proper edge crossings count as overlap.
    if (!intersects(p1, q1, p2, q2)) return false;
    if (
      pointOnSegment(p1, p2, q2) ||
      pointOnSegment(q1, p2, q2) ||
      pointOnSegment(p2, p1, q1) ||
      pointOnSegment(q2, p1, q1)
    )
      return false;
    return true;
  };
  const pipStrict = (point: number[], polygon: number[][]) => {
    for (let i = 0; i < polygon.length; i++) {
      if (pointOnSegment(point, polygon[i], polygon[(i + 1) % polygon.length])) {
        return false; // touching boundary is valid adjacency
      }
    }
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      const hit =
        yi > point[1] !== yj > point[1] &&
        point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi || 1e-12) + xi;
      if (hit) inside = !inside;
    }
    return inside;
  };

  for (let i = 0; i < a.length; i++) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      if (strictIntersects(a1, a2, b1, b2)) return true;
    }
  }

  return pipStrict(a[0], b) || pipStrict(b[0], a);
};

interface LayoutsViewProps {
  // Layout state
  selectedLayoutId: string;
  setSelectedLayoutId: (id: string) => void;
  layouts: Layout[];
  layoutsLoading: boolean;
  layoutsError: unknown;
  retryLayouts: () => void;
  layoutDataLoading: boolean;
  layoutDataFetching: boolean;
  isLayoutAssetLoading: boolean;

  // Display data
  imagePath: string;
  pdfPath: string | null;
  pdfPageNumber: number;
  pdfRotation: number;
  pdfCropRegion: [number, number, number, number] | null;
  pdfRasterFallback?: PdfRasterFallback;
  flippedHorizontal?: boolean;
  flippedVertical?: boolean;
  isPdfLayout: boolean;
  imageDimensions: ImageDimensions;
  areaTags: Unit[];
  displayPhotoMarkers: PhotoMarker[];
  layoutHotspots: LayoutHotspotListItem[];
  projectId: string;

  // Interaction handlers
  getCursor: () => string;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleSvgClick: (e: React.MouseEvent) => void;
  handleUnitClick: (unit: Unit, event: React.MouseEvent) => void;
  handleMarkerClick: (marker: PhotoMarker, event: React.MouseEvent) => void;
  deletePhotoMarker: (markerId: number) => void;
  handleMarkerRightClick?: (event: React.MouseEvent) => void;
  handleMarkerDragStart?: (markerId: number, event: React.MouseEvent) => void;
  onWheelZoom: (e: React.WheelEvent) => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
  // Used by the shared PDF stage for click-to-place functionality.
  addPhotoMarker: (point: Position) => void;

  // Control functions
  zoom: (factor: number) => void;
  reset: () => void;
  mode: 'view' | 'measure' | 'photoIndex' | 'areaTags';
  setMode: (mode: 'view' | 'measure' | 'photoIndex' | 'areaTags') => void;
  photoIndexMode: boolean;
  setCurrentPolygon: (polygon: number[][]) => void;
  closePolygon: () => void;
  clearPolygon: () => void;
  savePhotoMarkers: () => void;
  setPhotoIndexMode: (value: boolean) => void;
  setSelectedMarkers: (markers: Set<number>) => void;
  setShowMarkerToolbar: (value: boolean) => void;
  setSelectedUnit: (unit: Unit | null) => void;
  setImageDimensions: (dimensions: ImageDimensions) => void;
  completeLayoutAssetLoad: (layoutId: string) => void;

  // SVG refs and state
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  position: Position;
  scale: number;
  setPosition: (position: Position) => void;
  setScale: (scale: number) => void;
  selectedUnit: Unit | null;
  currentPolygon: number[][];
  selectedMarkers: Set<number>;
  showCoordinates: boolean;

  // StatusBar specific props
  savePhotoMarkersMutation: UseMutationResult<SaveMarkersResponse, Error, SaveMarkersData, unknown>;
  isHorizontalConstrained: boolean;
  isVerticalConstrained: boolean;
}

const LayoutsView: React.FC<LayoutsViewProps> = ({
  selectedLayoutId,
  setSelectedLayoutId,
  layouts,
  layoutsLoading,
  layoutsError,
  retryLayouts,
  layoutDataLoading,
  isLayoutAssetLoading,
  imagePath,
  pdfPath,
  pdfPageNumber,
  pdfRotation,
  pdfCropRegion,
  pdfRasterFallback,
  flippedHorizontal = false,
  flippedVertical = false,
  isPdfLayout,
  imageDimensions,
  areaTags,
  displayPhotoMarkers,
  layoutHotspots,
  projectId,
  getCursor,
  handleMouseDown,
  handleSvgClick,
  handleUnitClick,
  handleMarkerClick,
  deletePhotoMarker,
  handleMarkerRightClick,
  handleMarkerDragStart,
  onWheelZoom,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  addPhotoMarker,
  zoom,
  reset: resetFromParent,
  mode,
  setMode,
  photoIndexMode,
  setCurrentPolygon,
  closePolygon,
  clearPolygon,
  savePhotoMarkers,
  setPhotoIndexMode,
  setSelectedMarkers,
  setShowMarkerToolbar,
  setSelectedUnit,
  setImageDimensions,
  completeLayoutAssetLoad,
  svgRef,
  groupRef,
  position,
  scale,
  setPosition,
  setScale,
  selectedUnit,
  currentPolygon,
  selectedMarkers,
  showCoordinates,
  savePhotoMarkersMutation,
  isHorizontalConstrained,
  isVerticalConstrained,
}) => {
  // Background query refreshes must not hide an already usable layout. PDF
  // rendering owns its local loading/error state inside LayoutCanvas.
  const isSceneLoading = layoutsLoading || layoutDataLoading || isLayoutAssetLoading;
  const handleLayoutAssetReady = useCallback(
    () => completeLayoutAssetLoad(selectedLayoutId),
    [completeLayoutAssetLoad, selectedLayoutId]
  );

  const previousModeRef = useRef(mode);

  // Stores the auto-fit values when a PDF layout is loaded, so reset() restores the fit view.
  const pdfFitRef = useRef<{ scale: number; position: Position } | null>(null);
  const hasAppliedInitialPdfFitRef = useRef(false);
  const pdfCropKey = useMemo(
    () => (pdfCropRegion ? pdfCropRegion.join(',') : 'none'),
    [pdfCropRegion]
  );

  // When the active PDF layout/page changes, drop the cached fit values
  // and re-arm the one-shot fit application. Without clearing pdfFitRef
  // the in-component reset button would apply the *previous* layout's
  // fit (zoom level + pan position) to the new layout, leaving the user
  // looking at a transform that doesn't match the new drawing.
  useEffect(() => {
    pdfFitRef.current = null;
    hasAppliedInitialPdfFitRef.current = false;
  }, [isPdfLayout, selectedLayoutId, pdfPath, pdfPageNumber, pdfCropKey]);

  const reset = useCallback(() => {
    // Preserve the normal reset semantics (clear selected area, polygon, and
    // transient viewer state), then restore the PDF's current overview fit.
    resetFromParent();
    if (!isPdfLayout) return;

    const overlay = svgRef.current;
    const pdfViewportRect = overlay
      ?.closest<HTMLElement>('.iv-pdf-page-viewport')
      ?.getBoundingClientRect();
    // The PDF stage owns the displayed dimensions (including crop/rotation and
    // mobile raster sizing). Host metadata can still contain placeholder sizes.
    const fit = resolvePdfOverviewFit(pdfViewportRect, overlay?.viewBox.baseVal, pdfFitRef.current);
    if (fit) {
      pdfFitRef.current = fit;
      setScale(fit.scale);
      setPosition(fit.position);
    }
  }, [isPdfLayout, resetFromParent, setPosition, setScale, svgRef]);

  // Text editing state for area tags
  const [textEditingMode, setTextEditingMode] = useState(false);

  // Polygon editing state for area tags
  const [polygonEditingMode, setPolygonEditingMode] = useState(false);

  // Add layout photo dialog
  const [showAddPhotoDialog, setShowAddPhotoDialog] = useState(false);
  const [isLabelDialogOpen, setIsLabelDialogOpen] = useState(false);
  const [activeLabelAreaId, setActiveLabelAreaId] = useState<number | null>(null);
  const [draftLabel, setDraftLabel] = useState('');
  const [isSavingLabel, setIsSavingLabel] = useState(false);
  const [isAreaTagSetDialogOpen, setIsAreaTagSetDialogOpen] = useState(false);
  const [areaTagSetName, setAreaTagSetName] = useState('');
  const [isDrawingAreaTagSetLasso, setIsDrawingAreaTagSetLasso] = useState(false);
  const [heatMapIssueFilter, setHeatMapIssueFilter] = useState<HeatMapIssueRange>('all');
  // The heat map is opt-in: it stays dormant (no area/marker coloring, no
  // legend, no tooltips) until the user activates it by clicking the
  // heat-map button in the bottom-right corner of the viewer. When inactive
  // only that button is shown.
  const [isHeatMapActive, setIsHeatMapActive] = useState(false);

  // Get project ID from property context
  const { selectedProperty } = useProperty();
  const contextProjectId = selectedProperty?.id || projectId;
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { toast } = useToast();
  const isSuperuser = Boolean(user?.is_superuser);
  const canEditAreaTags = Boolean(user);
  const deleteAreaTagMutation = useDeleteViewerAreaTag(selectedLayoutId, contextProjectId);
  const updateAreaTagMutation = useUpdateViewerAreaTag(selectedLayoutId, contextProjectId);
  const { data: areaTagSets = [] } = useAreaTagSets(selectedLayoutId, contextProjectId);
  const createAreaTagSetMutation = useCreateAreaTagSet(selectedLayoutId, contextProjectId);
  const selectedAreaTagsForSet = useMemo(
    () =>
      isDrawingAreaTagSetLasso ? [] : selectAreaTagsMostlyInsideLasso(areaTags, currentPolygon),
    [areaTags, currentPolygon, isDrawingAreaTagSetLasso]
  );
  const selectedAreaTagIdsForSet = useMemo(
    () => new Set(selectedAreaTagsForSet.map((areaTag) => areaTag.id)),
    [selectedAreaTagsForSet]
  );

  const handleCreateAreaTagSet = useCallback(async () => {
    const name = areaTagSetName.trim();
    if (!name || selectedAreaTagsForSet.length === 0) return;

    try {
      await createAreaTagSetMutation.mutateAsync({
        name,
        layout: Number(selectedLayoutId),
        area_tag_ids: selectedAreaTagsForSet.map((areaTag) => areaTag.id),
      });
      setAreaTagSetName('');
      setIsAreaTagSetDialogOpen(false);
      setCurrentPolygon([]);
      toast({
        title: 'Phase/zone created',
        description: `${name} includes ${selectedAreaTagsForSet.length} ${selectedAreaTagsForSet.length === 1 ? 'unit/location' : 'units/locations'}.`,
      });
    } catch {
      toast({
        title: 'Could not create phase/zone',
        description: 'Check the phase/zone name and selected units/locations, then try again.',
        variant: 'destructive',
      });
    }
  }, [
    areaTagSetName,
    createAreaTagSetMutation,
    selectedAreaTagsForSet,
    selectedLayoutId,
    setCurrentPolygon,
    toast,
  ]);

  const performPatch = useCallback(
    async (areaTagId: number, data: any) => {
      return updateAreaTagMutation.mutateAsync({
        areaTagId,
        data: {
          name: data.name,
          coordinates: data.coordinates?.map(([x, y]: [number, number]) => [x, y] as const),
          markerIds: data.marker_ids,
          photoIds: data.photo_ids,
          textPosition: data.text_position,
          textSize: data.text_size,
          textRotation: data.text_rotation,
        },
      });
    },
    [updateAreaTagMutation]
  );

  // Handle text editing mode change from overlay
  const handleTextEditingModeChange = useCallback((isTextEditingMode: boolean) => {
    setTextEditingMode(isTextEditingMode);
  }, []);

  // Handle polygon editing mode change from overlay
  const handlePolygonEditingModeChange = useCallback((isPolygonEditingMode: boolean) => {
    setPolygonEditingMode(isPolygonEditingMode);
  }, []);

  // Local text editing state (not saved until user clicks save)
  const [pendingTextChanges, setPendingTextChanges] = useState<
    Map<
      number,
      Partial<{
        name: string;
        text_position: [number, number];
        text_size: number;
        text_rotation: number;
      }>
    >
  >(new Map());

  // Local polygon editing state (not saved until user clicks save)
  const [pendingPolygonChanges, setPendingPolygonChanges] = useState<Map<number, number[][]>>(
    new Map()
  );

  const getAreaLabel = useCallback(
    (area: Unit) =>
      pendingTextChanges.get(area.id)?.name ||
      Object.keys(area.images || {})[0] ||
      `Area ${area.id}`,
    [pendingTextChanges]
  );

  const openLabelDialogForArea = useCallback(
    (areaId: number) => {
      const area = areaTags.find((item) => item.id === areaId);
      if (!area) return;
      setActiveLabelAreaId(areaId);
      setDraftLabel(getAreaLabel(area));
      setIsLabelDialogOpen(true);
    },
    [areaTags, getAreaLabel]
  );

  const closeLabelDialog = useCallback(() => {
    if (isSavingLabel) return;
    setIsLabelDialogOpen(false);
    setActiveLabelAreaId(null);
    setDraftLabel('');
  }, [isSavingLabel]);

  const handleLabelDialogSave = useCallback(async () => {
    if (!activeLabelAreaId) return;
    const nextName = draftLabel.trim();
    if (!nextName) {
      alert('Label cannot be empty.');
      return;
    }

    const previousEntry = pendingTextChanges.get(activeLabelAreaId);

    // Optimistic UI update
    setPendingTextChanges((prev) => {
      const next = new Map(prev);
      const existing = next.get(activeLabelAreaId) || {};
      next.set(activeLabelAreaId, { ...existing, name: nextName });
      return next;
    });
    setIsSavingLabel(true);

    try {
      await performPatch(activeLabelAreaId, { name: nextName });
      setIsLabelDialogOpen(false);
      setActiveLabelAreaId(null);
      setDraftLabel('');
    } catch (error) {
      // Roll back optimistic label change on failure
      setPendingTextChanges((prev) => {
        const next = new Map(prev);
        if (previousEntry) {
          next.set(activeLabelAreaId, previousEntry);
        } else {
          const current = next.get(activeLabelAreaId);
          if (current) {
            const { name: _removed, ...rest } = current;
            if (Object.keys(rest).length > 0) {
              next.set(activeLabelAreaId, rest);
            } else {
              next.delete(activeLabelAreaId);
            }
          }
        }
        return next;
      });
      alert('Failed to save label. Please try again.');
    } finally {
      setIsSavingLabel(false);
    }
  }, [activeLabelAreaId, draftLabel, pendingTextChanges, performPatch]);

  // Save all pending changes (both text and polygon)
  const handleSaveChanges = useCallback(async () => {
    const textChanges = Array.from(pendingTextChanges.entries());
    const polygonChanges = Array.from(pendingPolygonChanges.entries());

    if (textChanges.length === 0 && polygonChanges.length === 0) return;

    try {
      // Validate overlap locally before submit
      const resolvedPolygons = new Map<number, number[][]>();
      areaTags.forEach((tag) => {
        resolvedPolygons.set(tag.id, pendingPolygonChanges.get(tag.id) || tag.points);
      });
      for (let i = 0; i < areaTags.length; i++) {
        for (let j = i + 1; j < areaTags.length; j++) {
          const a = resolvedPolygons.get(areaTags[i].id) || [];
          const b = resolvedPolygons.get(areaTags[j].id) || [];
          if (polygonsOverlap(a, b)) {
            alert(
              `Cannot save polygon edits: "${Object.keys(areaTags[i].images || {})[0] || areaTags[i].id}" overlaps another area.`
            );
            return;
          }
        }
      }

      // Prepare all changes for saving
      const allChanges: Promise<any>[] = [];

      // Add text changes
      textChanges.forEach(([areaId, data]) => {
        allChanges.push(performPatch(areaId, data));
      });

      // Add polygon changes
      polygonChanges.forEach(([areaId, coordinates]) => {
        // Backend expects coordinates as array of arrays: [[x, y], [x, y], ...]
        allChanges.push(performPatch(areaId, { coordinates: coordinates }));
      });

      // Save all changes in parallel
      await Promise.all(allChanges);

      // Clear pending changes on success
      setPendingTextChanges(new Map());
      setPendingPolygonChanges(new Map());

      const totalChanges = textChanges.length + polygonChanges.length;
      alert(`${totalChanges} change(s) saved successfully!`);
    } catch (error) {
      console.error('Failed to save changes:', error);
      alert('Failed to save changes. Please try again.');
    }
  }, [pendingTextChanges, pendingPolygonChanges, performPatch, areaTags]);

  // Cancel pending changes
  const handleCancelChanges = useCallback(() => {
    setPendingTextChanges(new Map());
    setPendingPolygonChanges(new Map());
    setTextEditingMode(false);
    setPolygonEditingMode(false);
  }, []);

  // Handle polygon coordinate updates (local only - no server calls)
  const handlePolygonUpdate = useCallback((areaId: number, newCoordinates: number[][]) => {
    setPendingPolygonChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(areaId, newCoordinates);
      return newMap;
    });
  }, []);

  const handleDeleteAreaTag = useCallback(
    async (areaId: number) => {
      const area = areaTags.find((tag) => tag.id === areaId);
      const areaLabel = area ? getAreaLabel(area) : `Area ${areaId}`;
      const shouldDelete = window.confirm(`Delete "${areaLabel}"? This cannot be undone.`);
      if (!shouldDelete) return;

      try {
        await deleteAreaTagMutation.mutateAsync(areaId);
      } catch (error) {
        alert('Failed to delete area. Please try again.');
      }
    },
    [areaTags, deleteAreaTagMutation, getAreaLabel]
  );

  // Calculate which markers belong to areas plus area-photo mappings
  const { markersInAreas, areaPhotoMap, areaMarkerMap } = useMemo(() => {
    const markersInAreasSet = new Set<PhotoMarker>();
    const areaPhotoMapData = new Map<number, number[]>();
    const areaMarkerMapData = new Map<number, number[]>();

    areaTags.forEach((area) => {
      const areaPhotoIds: number[] = [];
      const areaMarkerIds = Array.isArray(area.marker_ids) ? area.marker_ids : [];

      // Get all photo IDs from the area's images
      Object.values(area.images).forEach((photos) => {
        photos.forEach((photo) => {
          areaPhotoIds.push(photo.id);
        });
      });

      areaPhotoMapData.set(area.id, areaPhotoIds);
      areaMarkerMapData.set(area.id, areaMarkerIds);

      // Find markers that are directly associated with this area. Fall back to
      // photo matching for older cached responses that do not include marker IDs.
      displayPhotoMarkers.forEach((marker) => {
        const markerDbId = marker.dbId;
        if (markerDbId !== undefined && areaMarkerIds.includes(markerDbId)) {
          markersInAreasSet.add(marker);
          return;
        }

        const hasMatchingPhoto = marker.photos.some((photo) => areaPhotoIds.includes(photo.id));
        if (hasMatchingPhoto) {
          markersInAreasSet.add(marker);
        }
      });
    });

    return {
      markersInAreas: markersInAreasSet,
      areaPhotoMap: areaPhotoMapData,
      areaMarkerMap: areaMarkerMapData,
    };
  }, [areaTags, displayPhotoMarkers]);

  const { areaHeatMapSummaries, markerHeatMapSummaries } = useMemo(() => {
    const countsByPhoto = buildPhotoIssueCounts(layoutHotspots);
    const hotspotsByPhoto = buildPhotoHotspotCounts(layoutHotspots);
    const areaSummaries = new Map<number, HeatMapSummary>();
    const markerSummaries = new Map<number, HeatMapSummary>();

    areaTags.forEach((area) => {
      const photoIds = areaPhotoMap.get(area.id) ?? [];
      const label = Object.keys(area.images || {})[0] || `Area ${area.id}`;
      areaSummaries.set(
        area.id,
        summarizePhotoIds(photoIds, countsByPhoto, hotspotsByPhoto, label)
      );
    });

    displayPhotoMarkers.forEach((marker) => {
      markerSummaries.set(
        marker.id,
        summarizePhotoIds(
          marker.photos.map((photo) => photo.id),
          countsByPhoto,
          hotspotsByPhoto,
          `Marker ${marker.number}`
        )
      );
    });

    return {
      areaHeatMapSummaries: areaSummaries,
      markerHeatMapSummaries: markerSummaries,
    };
  }, [areaTags, areaPhotoMap, displayPhotoMarkers, layoutHotspots]);

  // Unit navigation logic
  const sortedUnits = useMemo(() => {
    return [...areaTags].sort((a, b) => {
      // Sort by unit name/number for consistent navigation
      const aName = Object.keys(a.images || {})[0] || `Area ${a.id}`;
      const bName = Object.keys(b.images || {})[0] || `Area ${b.id}`;

      // Extract numbers for natural sorting (e.g., "10" comes after "2")
      const aNum = parseInt(aName.match(/\d+/)?.[0] || '0');
      const bNum = parseInt(bName.match(/\d+/)?.[0] || '0');

      return aNum - bNum;
    });
  }, [areaTags]);

  const currentUnitIndex = useMemo(() => {
    if (!selectedUnit) return -1;
    return sortedUnits.findIndex((unit) => unit.id === selectedUnit.id);
  }, [selectedUnit, sortedUnits]);

  const handleBackToOverview = useCallback(() => {
    reset();
  }, [reset]);

  // Enhanced area click handler for zoom and detail view
  const handleAreaClick = useCallback(
    (area: Unit, event?: React.MouseEvent) => {
      if (event) {
        event.stopPropagation();
      }

      if (mode === 'view') {
        const areaPoints = Array.isArray(area.points) ? area.points : [];
        if (areaPoints.length === 0) return;

        const areaPhotoIds = areaPhotoMap.get(area.id) ?? [];
        const areaMarkerIds = areaMarkerMap.get(area.id) ?? [];
        const associatedMarkers = displayPhotoMarkers.filter((marker) => {
          if (marker.dbId !== undefined && areaMarkerIds.includes(marker.dbId)) {
            return true;
          }
          const markerPhotoIds = marker.photos.map((photo) => photo.id);
          return areaPhotoIds.some((photoId) => markerPhotoIds.includes(photoId));
        });

        // IMPORTANT: camera math must use the same coordinate space as polygon points.
        // - PDF layouts: pixel-space overlay, so use rendered rect dimensions.
        // - Image layouts: SVG viewBox-space, so use viewBox dimensions.
        const svgEl = svgRef.current;
        const overlayRect = svgEl?.getBoundingClientRect();
        const pdfViewportRect = svgEl
          ?.closest<HTMLElement>('.iv-pdf-page-viewport')
          ?.getBoundingClientRect();
        const viewBox = svgEl?.viewBox?.baseVal;
        const {
          width: viewportWidth,
          height: viewportHeight,
          centerX: viewportCenterX,
          centerY: viewportCenterY,
        } = resolveAreaFocusViewport({
          isPdfLayout,
          pdfViewportRect,
          overlayRect,
          viewBox,
          fallback: imageDimensions,
        });

        // Leave a little breathing room for the area-local title/back control.
        const isMobileViewport = viewportWidth < 768;
        const sideInset = Math.max(12, Math.min(32, viewportWidth * 0.03));
        const visualViewport = typeof window !== 'undefined' ? window.visualViewport : undefined;
        const browserBottomChromeInset =
          isMobileViewport && visualViewport
            ? Math.max(0, window.innerHeight - visualViewport.height - visualViewport.offsetTop)
            : 0;
        const bottomInset = (isMobileViewport ? 28 : 20) + browserBottomChromeInset;
        const topInset = isMobileViewport
          ? Math.max(80, viewportHeight * 0.14)
          : Math.max(64, viewportHeight * 0.08);

        const focusTransform = calculateSelectedAreaFocusTransform({
          points: areaPoints.map(([x, y]) => [x, y] as const),
          markers: associatedMarkers,
          viewport: {
            width: viewportWidth,
            height: viewportHeight,
            centerX: viewportCenterX,
            centerY: viewportCenterY,
          },
          insets: {
            top: topInset,
            right: sideInset,
            bottom: bottomInset,
            left: sideInset,
          },
        });
        if (!focusTransform) return;

        setScale(focusTransform.scale);
        setPosition(focusTransform.position);

        const mockEvent =
          event ||
          ({
            stopPropagation: () => {},
            preventDefault: () => {},
          } as React.MouseEvent);
        handleUnitClick(area, mockEvent);
      }
    },
    [
      mode,
      imageDimensions.width,
      imageDimensions.height,
      isPdfLayout,
      areaPhotoMap,
      areaMarkerMap,
      displayPhotoMarkers,
      setScale,
      setPosition,
      handleUnitClick,
      svgRef,
    ]
  );

  // Navigate to previous unit
  const handlePreviousUnit = useCallback(() => {
    if (sortedUnits.length === 0) return;

    const prevIndex = currentUnitIndex <= 0 ? sortedUnits.length - 1 : currentUnitIndex - 1;

    const prevUnit = sortedUnits[prevIndex];
    if (prevUnit) {
      handleAreaClick(prevUnit);
    }
  }, [sortedUnits, currentUnitIndex, handleAreaClick]);

  // Navigate to next unit
  const handleNextUnit = useCallback(() => {
    if (sortedUnits.length === 0) return;

    const nextIndex = currentUnitIndex >= sortedUnits.length - 1 ? 0 : currentUnitIndex + 1;

    const nextUnit = sortedUnits[nextIndex];
    if (nextUnit) {
      handleAreaClick(nextUnit);
    }
  }, [sortedUnits, currentUnitIndex, handleAreaClick]);

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle arrow keys when a unit is selected and we're in view mode
      if (!selectedUnit || mode !== 'view' || sortedUnits.length <= 1) return;

      // Prevent default if we're handling the key
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();

        if (event.key === 'ArrowLeft') {
          handlePreviousUnit();
        } else if (event.key === 'ArrowRight') {
          handleNextUnit();
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedUnit, mode, sortedUnits.length, handlePreviousUnit, handleNextUnit]);

  // Get selected layout
  const selectedLayout = useMemo(() => {
    return layouts.find((l) => String(l.id) === selectedLayoutId);
  }, [layouts, selectedLayoutId]);
  // The heat map is available on every layout now (it used to be gated
  // behind the ``beta_features_enabled`` flag). We still require a loaded
  // layout so the entry-point button doesn't show on an empty viewer. This
  // only gates whether the button is offered — it does NOT mean the overlay
  // is showing.
  const isHeatMapEnabled = Boolean(selectedLayout);
  // The overlay (coloring + tooltips) only renders once the user activates
  // the heat map AND the layout supports it.
  const showHeatMapOverlay = isHeatMapEnabled && isHeatMapActive;

  // Reset the heat map back to dormant whenever the user switches layouts so
  // it never appears "on" without an explicit click on the new layout.
  useEffect(() => {
    setIsHeatMapActive(false);
    setHeatMapIssueFilter('all');
  }, [selectedLayoutId]);

  // Check if selected layout has a photo or PDF
  const hasLayoutPhoto = useMemo(() => {
    if (!selectedLayout) return true; // Hide button if no layout selected

    // Check for image (must be truthy and not empty string)
    const hasPhoto = Boolean(
      (selectedLayout.image_url && selectedLayout.image_url.trim()) ||
        (selectedLayout.signed_photo_url && selectedLayout.signed_photo_url.trim()) ||
        (selectedLayout.photo_url && selectedLayout.photo_url.trim())
    );

    // Check for PDF
    const hasPdf = Boolean(
      selectedLayout.has_pdf && selectedLayout.pdf_file_url && selectedLayout.pdf_file_url.trim()
    );

    return hasPhoto || hasPdf;
  }, [selectedLayout]);

  const handlePhotoUploadSuccess = useCallback(async () => {
    const currentLayoutId = selectedLayoutId;

    // Invalidate all layouts cache for this project (matches any type)
    await queryClient.invalidateQueries({
      queryKey: viewerQueryKeys.layoutLists(ironview360ViewerAdapter.id, contextProjectId),
    });

    if (currentLayoutId) {
      await queryClient.invalidateQueries({
        queryKey: viewerQueryKeys.markers(
          ironview360ViewerAdapter.id,
          contextProjectId,
          currentLayoutId
        ),
      });
    }

    // Small delay to ensure the backend has processed the upload
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Refetch the current layouts query
    await queryClient.refetchQueries({
      queryKey: viewerQueryKeys.layoutLists(ironview360ViewerAdapter.id, contextProjectId),
    });

    if (currentLayoutId) {
      await queryClient.refetchQueries({
        queryKey: viewerQueryKeys.markers(
          ironview360ViewerAdapter.id,
          contextProjectId,
          currentLayoutId
        ),
      });
    }

    // The refetch triggers the useEffect that loads the layout when the
    // layouts data changes (see useInteractiveViewer.ts).
    setShowAddPhotoDialog(false);
  }, [selectedLayoutId, queryClient, contextProjectId]);

  useEffect(() => {
    if (!canEditAreaTags && (mode === 'areaTags' || mode === 'measure')) {
      setMode('view');
    }
  }, [canEditAreaTags, mode, setMode]);

  useEffect(() => {
    const wasAreaTags = previousModeRef.current === 'areaTags';
    const enteredAreaTags = mode === 'areaTags' && !wasAreaTags;
    const wasAreaTagSets = previousModeRef.current === 'measure';
    const enteredAreaTagSets = mode === 'measure' && !wasAreaTagSets;
    const exitedAreaTagSets = mode !== 'measure' && wasAreaTagSets;

    if (enteredAreaTags || enteredAreaTagSets) {
      // Match Home-button behavior when entering a geometry editing mode.
      reset();
      setSelectedUnit(null);
    }
    if (exitedAreaTagSets) {
      setCurrentPolygon([]);
      setIsDrawingAreaTagSetLasso(false);
      setIsAreaTagSetDialogOpen(false);
      setAreaTagSetName('');
    }

    previousModeRef.current = mode;
  }, [mode, reset, setCurrentPolygon, setSelectedUnit]);

  const layoutCollectionState = getLayoutCollectionState({
    isLoading: layoutsLoading,
    error: layoutsError,
    layoutCount: layouts.length,
  });

  if (layoutCollectionState === 'error') {
    return (
      <ErrorState
        error={layoutsError}
        onRetry={retryLayouts}
        className="flex h-full items-center justify-center p-4 md:p-8"
      />
    );
  }

  // Only a successful empty response is allowed to render the empty state.
  if (layoutCollectionState === 'empty') {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="text-muted-foreground mb-4">
            <svg
              className="mx-auto h-16 w-16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No Layouts Yet</h3>
          <p className="text-sm text-muted-foreground">
            This project doesn't have any layouts yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className={isSceneLoading ? 'invisible pointer-events-none' : undefined}>
        <TopControls
          selectedLayout={selectedLayoutId}
          setSelectedLayout={setSelectedLayoutId}
          layouts={layouts}
          zoom={zoom}
          reset={reset}
          mode={mode}
          setMode={setMode}
          photoIndexMode={photoIndexMode}
          setCurrentPolygon={setCurrentPolygon}
          closePolygon={closePolygon}
          clearPolygon={clearPolygon}
          savePhotoMarkers={savePhotoMarkers}
          setPhotoIndexMode={setPhotoIndexMode}
          setSelectedMarkers={setSelectedMarkers}
          setShowMarkerToolbar={setShowMarkerToolbar}
          svgRef={svgRef}
          isPdfLayout={isPdfLayout}
          pdfPath={pdfPath}
          pdfPageNumber={pdfPageNumber}
          pdfRotation={pdfRotation}
          pdfCropRegion={pdfCropRegion}
          flippedHorizontal={flippedHorizontal}
          flippedVertical={flippedVertical}
          photoMarkers={displayPhotoMarkers}
          hasAreaTags={areaTags.length > 0}
        />
      </div>

      {/* Instructions Bar - Second line under top bar, only in areaTags mode */}
      {mode === 'areaTags' && (
        <div className="bg-card border-b border-border px-4 py-2">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs sm:text-sm text-muted-foreground text-center leading-relaxed">
              <span className="hidden lg:inline">
                {areaTags.length > 0
                  ? 'Click to place points • 🟠 Snap to vertices • 🟡 Snap to edges • 🟢 Close area • Esc to cancel'
                  : 'Click to place points • 🟢 Close area • Esc to cancel • Create areas to enable edge snapping'}
              </span>
              <span className="hidden sm:inline lg:hidden">
                {areaTags.length > 0
                  ? 'Click to place • 🟠 Vertices • 🟡 Edges • 🟢 Close • Esc cancel'
                  : 'Click to place • 🟢 Close • Esc cancel'}
              </span>
              <span className="sm:hidden">
                {areaTags.length > 0 ? 'Tap • 🟠🟡🟢 • Esc' : 'Tap to place • 🟢 Close'}
              </span>
            </p>
          </div>
        </div>
      )}

      {mode === 'measure' && (
        <div className="border-b border-border bg-card px-4 py-2.5">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-3">
            <p className="text-center text-xs leading-relaxed text-muted-foreground sm:text-sm">
              Press and drag a rough loop around the units/locations you want, then release. Any
              unit/location mostly inside will be selected.
              {isDrawingAreaTagSetLasso ? (
                <span className="ml-2 font-semibold text-primary">Drawing…</span>
              ) : currentPolygon.length >= 3 ? (
                <span className="ml-2 font-semibold text-foreground">
                  {selectedAreaTagsForSet.length} selected
                </span>
              ) : null}
              <span className="ml-2 text-muted-foreground/80">
                {areaTagSets.length} existing{' '}
                {areaTagSets.length === 1 ? 'phase/zone' : 'phases/zones'}
              </span>
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCurrentPolygon([])}
                disabled={currentPolygon.length === 0}
              >
                Clear
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => setIsAreaTagSetDialogOpen(true)}
                disabled={currentPolygon.length < 3 || selectedAreaTagsForSet.length === 0}
              >
                Create phase/zone
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          className="relative min-h-0 flex-1 overflow-hidden"
          style={{ zIndex: 1 }}
          data-tour-id="iv-viewer-container"
        >
          {/* Add Layout Photo Button Overlay - OUTSIDE MainContentArea */}
          {!isSceneLoading &&
            !hasLayoutPhoto &&
            selectedLayout &&
            !selectedUnit &&
            mode !== 'areaTags' &&
            !imagePath &&
            !pdfPath && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-muted/90"
                style={{ zIndex: 10 }}
              >
                <Button
                  onClick={() => {
                    setShowAddPhotoDialog(true);
                  }}
                  className="shadow-2xl bg-blue-600 hover:bg-blue-700 text-white"
                  size="lg"
                >
                  <ImagePlus className="mr-2 h-5 w-5" />
                  Add Layout Photo
                </Button>
              </div>
            )}

          {/* Mount the scene behind the loader so assets can render and report readiness. */}
          <div
            className={`absolute inset-0 transition-opacity duration-200 ease-out ${
              isSceneLoading ? 'opacity-0' : 'opacity-100'
            }`}
          >
            {imagePath || pdfPath || !isSceneLoading ? (
              <LayoutCanvas
                selectedLayoutId={selectedLayoutId}
                getCursor={getCursor}
                imageDimensions={imageDimensions}
                handleMouseDown={handleMouseDown}
                handleSvgClick={handleSvgClick}
                units={areaTags}
                photoMarkers={displayPhotoMarkers}
                svgRef={svgRef}
                groupRef={groupRef}
                position={position}
                scale={scale}
                imagePath={imagePath}
                pdfUrl={isPdfLayout ? (pdfPath ?? undefined) : undefined}
                pdfPageNumber={pdfPageNumber}
                pdfRotation={pdfRotation}
                pdfCropRegion={pdfCropRegion}
                pdfRasterFallback={pdfRasterFallback}
                onDimensionsLoad={setImageDimensions}
                onFitComplete={(fitScale, fitPos) => {
                  pdfFitRef.current = { scale: fitScale, position: fitPos };
                  // Apply viewport auto-fit only once per PDF load. After that, keep user camera changes.
                  if (!hasAppliedInitialPdfFitRef.current) {
                    hasAppliedInitialPdfFitRef.current = true;
                    setScale(fitScale);
                    setPosition(fitPos);
                  }
                }}
                onScaleChange={setScale}
                onPositionChange={setPosition}
                addPhotoMarker={addPhotoMarker}
                setSelectedMarkers={setSelectedMarkers}
                flippedHorizontal={flippedHorizontal}
                flippedVertical={flippedVertical}
                selectedUnit={selectedUnit}
                currentPolygon={currentPolygon}
                selectedMarkers={selectedMarkers}
                markersInAreas={markersInAreas}
                areaPhotoMap={areaPhotoMap}
                areaMarkerMap={areaMarkerMap}
                areaHeatMapSummaries={areaHeatMapSummaries}
                markerHeatMapSummaries={markerHeatMapSummaries}
                heatMapIssueFilter={heatMapIssueFilter}
                isHeatMapEnabled={showHeatMapOverlay}
                photoIndexMode={photoIndexMode}
                showCoordinates={showCoordinates}
                mode={mode}
                handleUnitClick={handleAreaClick}
                handleMarkerClick={handleMarkerClick}
                deletePhotoMarker={deletePhotoMarker}
                handleMarkerRightClick={handleMarkerRightClick}
                handleMarkerDragStart={handleMarkerDragStart}
                onWheelZoom={onWheelZoom}
                handleTouchStart={handleTouchStart}
                handleTouchMove={handleTouchMove}
                handleTouchEnd={handleTouchEnd}
                disableMarkerClicks={mode === 'areaTags'}
                textEditingMode={textEditingMode}
                onRequestLabelEdit={openLabelDialogForArea}
                pendingTextChanges={pendingTextChanges}
                onSaveTextChanges={handleSaveChanges}
                onCancelTextChanges={handleCancelChanges}
                polygonEditingMode={polygonEditingMode}
                onPolygonUpdate={handlePolygonUpdate}
                pendingPolygonChanges={pendingPolygonChanges}
                showAreaDeleteControls={canEditAreaTags && polygonEditingMode}
                onDeleteAreaTag={handleDeleteAreaTag}
                areaTagSetSelection={mode === 'measure' ? selectedAreaTagIdsForSet : undefined}
                onBackToOverview={handleBackToOverview}
                onReady={handleLayoutAssetReady}
              />
            ) : null}
          </div>

          {!isSceneLoading && mode === 'measure' && canEditAreaTags && (
            <AreaTagSetLassoInput
              svgRef={svgRef}
              groupRef={groupRef}
              onChange={setCurrentPolygon}
              onDrawingChange={setIsDrawingAreaTagSetLasso}
              onWheel={onWheelZoom}
            />
          )}

          {/* Area Tags Drawing Overlay - Only show when in area tags mode */}
          {!isSceneLoading && mode === 'areaTags' && canEditAreaTags && (
            <Ironview360AreaTagControls
              selectedLayoutId={selectedLayoutId}
              projectId={contextProjectId}
              layouts={layouts}
              isPdfLayout={isPdfLayout}
              imageDimensions={imageDimensions}
              position={position}
              scale={scale}
              onWheelZoom={onWheelZoom}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTextEditingModeChange={handleTextEditingModeChange}
              onSaveTextChanges={handleSaveChanges}
              onCancelTextChanges={handleCancelChanges}
              onPolygonEditingModeChange={handlePolygonEditingModeChange}
              pendingPolygonChanges={pendingPolygonChanges}
            />
          )}

          {isHeatMapEnabled && mode !== 'areaTags' && !isHeatMapActive && (
            <button
              type="button"
              onClick={() => {
                setHeatMapIssueFilter('all');
                setIsHeatMapActive(true);
              }}
              aria-label="Show heat map"
              title="Show heat map"
              data-tour-id="iv-heatmap-toggle"
              className="group absolute bottom-4 right-4 z-30 flex h-12 w-12 items-center justify-center rounded-full bg-foreground/10 text-white shadow-lg ring-1 ring-foreground/10 transition-transform duration-200 ease-out hover:scale-110 focus-visible:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40"
            >
              {/* Soft animated halo behind the button \u2014 sized larger than
                  the button so the breathing glow extends well past the edge. */}
              <span
                aria-hidden="true"
                className="heatmap-fab-halo absolute -inset-3 -z-10 rounded-full blur-xl"
                style={{
                  background:
                    'linear-gradient(135deg, var(--heatmap-none) 0%, var(--heatmap-none) 14%, var(--heatmap-low) 40%, var(--heatmap-medium) 62%, var(--heatmap-high) 88%, var(--heatmap-high) 100%)',
                }}
              />
              {/* Animated colour wash + shimmer sweep, clipped to the circle.
                  Wrapped so the badge dot below isn't clipped. */}
              <span aria-hidden="true" className="absolute inset-0 overflow-hidden rounded-full">
                <span
                  className="heatmap-fab-gradient absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(135deg, var(--heatmap-none) 0%, var(--heatmap-none) 14%, var(--heatmap-low) 40%, var(--heatmap-medium) 62%, var(--heatmap-high) 88%, var(--heatmap-high) 100%)',
                  }}
                />
                <span
                  className="heatmap-fab-shimmer absolute inset-y-0 left-0 w-1/2"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)',
                  }}
                />
              </span>
              <Thermometer
                className="relative z-10 h-5 w-5 drop-shadow-sm transition-transform duration-200 group-hover:rotate-[-6deg]"
                strokeWidth={2.25}
              />
            </button>
          )}

          {/* Loading overlays */}
          {isSceneLoading && (
            <div className="absolute inset-0 bg-background flex items-center justify-center z-50">
              <LogoLoading variant="breathing" size="lg" message="Loading layouts..." />
            </div>
          )}
        </div>

        {isHeatMapEnabled && mode !== 'areaTags' && isHeatMapActive && (
          <HeatMapLegend
            filter={heatMapIssueFilter}
            onFilterChange={setHeatMapIssueFilter}
            onClose={() => setIsHeatMapActive(false)}
          />
        )}
      </div>

      {/* StatusBar - only shown on Layouts tab */}
      <StatusBar
        mode={mode}
        scale={scale}
        photoIndexMode={photoIndexMode}
        selectedMarkers={selectedMarkers}
        savePhotoMarkersMutation={savePhotoMarkersMutation}
        currentPolygon={currentPolygon}
        isHorizontalConstrained={isHorizontalConstrained}
        isVerticalConstrained={isVerticalConstrained}
      />

      {/* Add Layout Photo Dialog */}
      {selectedLayout && (
        <AddLayoutPhotoDialog
          open={showAddPhotoDialog}
          layoutId={selectedLayoutId}
          layoutName={selectedLayout.name}
          projectId={contextProjectId}
          layouts={layouts}
          isSuperuser={isSuperuser}
          onOpenChange={setShowAddPhotoDialog}
          onSuccess={handlePhotoUploadSuccess}
        />
      )}

      <Dialog
        open={isLabelDialogOpen}
        onOpenChange={(open) => (!open ? closeLabelDialog() : setIsLabelDialogOpen(true))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Unit Label</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isSavingLabel) {
                  handleLabelDialogSave();
                }
              }}
              placeholder="Enter unit label"
              autoFocus
              disabled={isSavingLabel}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeLabelDialog} disabled={isSavingLabel}>
              Cancel
            </Button>
            <Button onClick={handleLabelDialogSave} disabled={isSavingLabel || !draftLabel.trim()}>
              {isSavingLabel ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isAreaTagSetDialogOpen}
        onOpenChange={(open) => {
          if (createAreaTagSetMutation.isPending) return;
          setIsAreaTagSetDialogOpen(open);
          if (!open) setAreaTagSetName('');
        }}
      >
        <DialogContent className="rounded-[1.375rem] border-border bg-card">
          <DialogHeader>
            <DialogTitle>Create Phase/Zone</DialogTitle>
            <DialogDescription>
              Name this phase/zone containing {selectedAreaTagsForSet.length} selected{' '}
              {selectedAreaTagsForSet.length === 1 ? 'unit/location' : 'units/locations'}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={areaTagSetName}
              onChange={(event) => setAreaTagSetName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && areaTagSetName.trim()) {
                  void handleCreateAreaTagSet();
                }
              }}
              placeholder="Phase 1"
              autoFocus
              disabled={createAreaTagSetMutation.isPending}
            />
            <div className="flex max-h-32 flex-wrap gap-2 overflow-y-auto rounded-xl border border-border bg-muted/50 p-3">
              {selectedAreaTagsForSet.map((areaTag) => (
                <span
                  key={areaTag.id}
                  className="rounded-full bg-primary/12 px-2.5 py-1 text-xs font-semibold text-foreground ring-1 ring-primary/30"
                >
                  {Object.keys(areaTag.images || {})[0] || `Area ${areaTag.id}`}
                </span>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAreaTagSetDialogOpen(false)}
              disabled={createAreaTagSetMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleCreateAreaTagSet()}
              disabled={
                createAreaTagSetMutation.isPending ||
                !areaTagSetName.trim() ||
                selectedAreaTagsForSet.length === 0
              }
            >
              {createAreaTagSetMutation.isPending ? 'Creating...' : 'Create phase/zone'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LayoutsView;
